import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import api from '../lib/api';
import {
  CalendarCheck, Loader2, CheckCircle2, XCircle, Clock, Video,
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

function fmtWhen(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '';
  return d.toLocaleString(undefined, {
    weekday: 'short', month: 'short', day: 'numeric',
    hour: 'numeric', minute: '2-digit',
  });
}

// Public confirmation page for Brain-proposed meetings. GET loads details
// (never auto-confirms); the explicit Confirm button POSTs to finalise.
export default function MeetingConfirm() {
  const { token } = useParams();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [status, setStatus] = useState(null);     // proposed | confirmed | expired | not_found
  const [confirming, setConfirming] = useState(false);
  const [confirmed, setConfirmed] = useState(null); // {meeting_link, start_time, meeting_type_name}

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get(`${API}/api/meetings/confirm/${token}`);
      setData(r.data);
      setStatus(r.data?.status || 'proposed');
      if (r.data?.status === 'confirmed') {
        setConfirmed({
          meeting_link: r.data?.meeting?.meeting_link,
          start_time: r.data?.meeting?.start_time,
          meeting_type_name: r.data?.meeting?.meeting_type_name,
        });
      }
    } catch (e) {
      setStatus(e.response?.status === 404 ? 'not_found' : 'error');
    } finally { setLoading(false); }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const doConfirm = async () => {
    setConfirming(true);
    try {
      const r = await api.post(`${API}/api/meetings/confirm/${token}`);
      setConfirmed(r.data);
      setStatus('confirmed');
    } catch (e) {
      if (e.response?.status === 410) setStatus('expired');
      else setStatus('error');
    } finally { setConfirming(false); }
  };

  const company = data?.company_name || 'Lockene';
  const m = data?.meeting || {};

  return (
    <div className="min-h-screen flex items-center justify-center bg-[hsl(220,14%,97%)] dark:bg-background px-4 py-12" data-testid="meeting-confirm-page">
      <div className="w-full max-w-md rounded-2xl border border-border bg-card shadow-xl p-8 text-center">
        <div className="w-14 h-14 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-5">
          <CalendarCheck className="w-7 h-7 text-primary" />
        </div>

        {loading ? (
          <div className="flex flex-col items-center gap-3 py-6" data-testid="meeting-confirm-loading">
            <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
            <p className="text-sm text-muted-foreground">Loading your meeting…</p>
          </div>
        ) : status === 'confirmed' ? (
          <div data-testid="meeting-confirm-success">
            <CheckCircle2 className="w-10 h-10 text-emerald-500 mx-auto mb-3" />
            <h1 className="text-xl font-bold text-foreground mb-1">You're confirmed!</h1>
            <p className="text-sm text-muted-foreground mb-5">
              Your {confirmed?.meeting_type_name || m.meeting_type_name} with {company} is set for{' '}
              <span className="font-medium text-foreground">{fmtWhen(confirmed?.start_time || m.start_time)}</span>.
            </p>
            {confirmed?.meeting_link && (
              <a href={confirmed.meeting_link} target="_blank" rel="noreferrer"
                data-testid="meeting-confirm-join"
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-primary text-primary-foreground font-semibold hover:bg-primary/90 transition-colors">
                <Video className="w-4 h-4" /> Join link
              </a>
            )}
            <p className="text-xs text-muted-foreground mt-4">A calendar invite is on its way to your inbox.</p>
          </div>
        ) : status === 'expired' ? (
          <div data-testid="meeting-confirm-expired">
            <Clock className="w-10 h-10 text-amber-500 mx-auto mb-3" />
            <h1 className="text-xl font-bold text-foreground mb-1">This request has expired</h1>
            <p className="text-sm text-muted-foreground">
              This confirmation link is no longer valid. Please reach out to {company} to re-book.
            </p>
          </div>
        ) : status === 'not_found' ? (
          <div data-testid="meeting-confirm-notfound">
            <XCircle className="w-10 h-10 text-red-500 mx-auto mb-3" />
            <h1 className="text-xl font-bold text-foreground mb-1">Link not found</h1>
            <p className="text-sm text-muted-foreground">This confirmation link is invalid or has already been used.</p>
          </div>
        ) : status === 'error' ? (
          <div data-testid="meeting-confirm-error">
            <XCircle className="w-10 h-10 text-red-500 mx-auto mb-3" />
            <h1 className="text-xl font-bold text-foreground mb-1">Something went wrong</h1>
            <button onClick={load} className="mt-3 text-sm text-primary hover:underline">Try again</button>
          </div>
        ) : (
          <div data-testid="meeting-confirm-proposed">
            <h1 className="text-xl font-bold text-foreground mb-1">Confirm your meeting</h1>
            <p className="text-sm text-muted-foreground mb-1">
              {m.meeting_type_name} with {company}
            </p>
            <p className="text-base font-semibold text-foreground mb-6" data-testid="meeting-confirm-when">
              {fmtWhen(m.start_time)}
            </p>
            <button onClick={doConfirm} disabled={confirming}
              data-testid="meeting-confirm-btn"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-primary text-primary-foreground font-semibold hover:bg-primary/90 disabled:opacity-50 transition-colors w-full justify-center">
              {confirming ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
              Yes, confirm this meeting
            </button>
            <p className="text-xs text-muted-foreground mt-4">
              You'll get a calendar invite and join link once confirmed.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
